import type { ReactNode } from 'react';
import { Navigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useIsAdmin } from '@/hooks/useIsAdmin';

interface AdminGuardProps {
  children: ReactNode;
}

export default function AdminGuard({ children }: AdminGuardProps) {
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, needsEmailVerification, loading: adminLoading } = useIsAdmin();
  const location = useLocation();

  if (authLoading || adminLoading) {
    return (
      <div className="app">
        <div className="main" style={{ display: 'grid', placeItems: 'center', minHeight: '60vh' }}>
          <div style={{ color: 'var(--text-muted)', fontFamily: 'var(--mono)', letterSpacing: '0.18em', fontSize: 11 }}>
            ◊ ◊ ◊
          </div>
        </div>
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/signin" replace state={{ from: location.pathname }} />;
  }
  if (needsEmailVerification) {
    return (
      <div className="app">
        <div className="main">
          <div style={{ maxWidth: 520, margin: '60px auto', textAlign: 'center' }}>
            <div className="eyebrow">Admin Access · Verification Required</div>
            <h1 className="h-display" style={{ fontSize: 48 }}>One more step.</h1>
            <p className="lead" style={{ margin: '0 auto 24px' }}>
              Your email matches an admin account, but it isn't verified yet. Open your account
              page, send the verification email, click the link, then come back here.
            </p>
            <Link to="/account" className="btn">Go to Account →</Link>
          </div>
        </div>
      </div>
    );
  }
  if (!isAdmin) {
    return (
      <div className="app">
        <div className="main">
          <div style={{ maxWidth: 480, margin: '80px auto', textAlign: 'center' }}>
            <div className="eyebrow">403 · Forbidden</div>
            <h1 className="h-display" style={{ fontSize: 88, marginBottom: 16 }}>403</h1>
            <p className="lead" style={{ margin: '0 auto 24px' }}>
              This area is restricted to SWC operators.
            </p>
            <Link to="/" className="btn">← Back home</Link>
          </div>
        </div>
      </div>
    );
  }
  return <>{children}</>;
}
